// src/lib/depots-helpers.js
// Helpers pour le modèle stock C3.5 (refonte 2026-04-29) :
// - fbz8-depots-v2 : sorties stock central → logement
// - fbz8-achats    : entrées admin (réapprovisionnement stock central)
// - fbz8-stock-central : { qtyDispo, seuilMin } par produit
//
// Convention C3.5 :
//   * qtyDispo et nbPrelevables (lignes de dépôt) sont en UNITÉ PRÉLEVABLE
//     (= unité physique manipulée par le ménage : 1 paquet de 24 madeleines,
//     1 sous-paquet de 100 g, 1 pack de 6 bouteilles, 1 sous-paquet de 6 PQ).
//   * 1 paquet acheté = qtyParAchat / qtyParPrelevable unités prélevables.
//   * prixUnitePrelevable = prixAchat × (qtyParPrelevable / qtyParAchat)
//     = prix d'une unité prélevable.

const _qtyAchat = p => (p && (p.qtyParAchat ?? p.contenance)) || 1
const _qtyPrelevable = p => p && (p.qtyParPrelevable ?? _qtyAchat(p))

// Les deux mêmes, exportées : l'écran doit lire la MÊME définition que le
// serveur (repli `contenance` compris), pas une variante locale.
export const qtyAchat = _qtyAchat
export const qtyPrelevable = p => _qtyPrelevable(p) || 1

// ══ L'ÉCHELLE D'UN ACHAT — MIROIR DE `lib/produits-helpers.js` (revue adverse
// #1449, MAJEUR 2, 2026-09-16) ═══════════════════════════════════════════════
//
// Combien d'unités PRÉLEVABLES un paquet acheté ajoute-t-il au stock central ?
// La règle vivait en deux endroits qui ne disaient pas la même chose :
// `SaisirAchatModal` lisait `qtyParAchat ?? 1` (SANS le repli `contenance`)
// pendant que le serveur lisait `qtyParAchat ?? contenance`. Un produit
// pré-migration `{contenance: 48, qtyParPrelevable: 6}` donnait scale=1 à
// l'écran et scale=8 au serveur. La modale importe désormais cette fonction ;
// `src/lib/produits-echelle-parite.test.js` interdit aux deux copies de
// diverger (CRA refuse un import hors de `src/`, d'où le miroir).
//
// `mode` : "A" = le paquet entier, "P" = un sous-paquet. Sans effet si le
// paquet ne se divise pas.
export function echelleAchatPrelevable(p, mode) {
  const qtyA = _qtyAchat(p)
  const qtyP = qtyPrelevable(p)
  if (!(qtyA > qtyP)) return 1
  return mode === "A" ? qtyA / qtyP : 1
}

// Nombre d'unités prélevables par achat (= sous-paquets dans 1 carton).
export function nbPrelevablesParAchat(p) {
  const qtyP = qtyPrelevable(p)
  return qtyP ? _qtyAchat(p) / qtyP : 1
}

// Prix d'une unité prélevable.
// Madeleine (qtyA=24, qtyP=24, prixAchat=2.41) → 2.41 €
// Sucre (qtyA=1000, qtyP=100, prixAchat=2.00) → 0.20 €
// PQ (qtyA=48, qtyP=6, prixAchat=12.00) → 1.50 €
export function prixUnitePrelevable(p) {
  if (!p || !p.prixAchat) return 0
  const qtyA = _qtyAchat(p)
  return qtyA ? p.prixAchat * (_qtyPrelevable(p) / qtyA) : 0
}

// Coût des dépôts d'un logement pour un mois donné.
// Utilise le coutTotal figé au save de chaque dépôt (pas de recalcul depuis cfg).
// Inputs :
//   logId  : id logement feuille (ex "l1", "lc2")
//   mois   : "YYYY-MM"
//   depots : array fbz8-depots-v2
// Output : montant arrondi 2 décimales.
export function coutDepotsLog(logId, mois, depots) {
  if (!logId || !mois) return 0
  const total = (depots || [])
    .filter(d => d.logId === logId && d.mois === mois)
    .reduce((sum, d) => sum + (Number(d.coutTotal) || 0), 0)
  return Math.round(total * 100) / 100
}

// Coût consommables RÉEL de TOUS les logements pour un mois donné = somme des
// dépôts (fbz8-depots-v2) imputés à chaque logement sur ce mois (coutTotal figé
// au save, ADR-210). Utilisé par le graphe P&L annuel de ComptaTab pour fournir
// le montant PAR MOIS — remplace l'ancien champ MORT compta.consommables[mois]
// (jamais écrit → toujours 0). `logs` = logements feuilles ({id}).
export function coutDepotsMoisTousLogs(logs, mois, depots) {
  if (!Array.isArray(logs) || !mois) return 0
  return Math.round(logs.reduce((s, l) => s + coutDepotsLog(l.id, mois, depots), 0) * 100) / 100
}

// Vrai si le mois passé est antérieur au mois courant (verrouillage écriture).
// mois format "YYYY-MM".
export function isMoisClos(mois) {
  if (!mois || !/^\d{4}-\d{2}$/.test(mois)) return false
  const now = new Date()
  const moisCourant = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`
  return mois < moisCourant
}
